/************************************** THIRD-PARTY MODULES ***************************************/
import {observable, action, computed} from 'mobx';
import {observer, inject} from 'mobx-react';
import {Int, arrayN, switchExpr} from 'mad-utils/lib/react';

/**************************************** PROJECT MODULES *****************************************/
import {RecordModel} from './record-model';
import {PagesModel} from './pages-model';

import {PageInstance, Page, DoseInstance, LocalDate, PageType} from '@canimmunize/javelinscript';

import {TabIDs} from '@canimmunize/core-imports';

/**************************************** TYPE DEFINITIONS ****************************************/
type UpOrDown = 'down' | 'up';

export interface DoseGroup {
    date: LocalDate;
    doses: DoseInstance[];
}

/********************************************* CONFIG *********************************************/
/**
 * Total number of UI tabs (Childhood, Adult, ..., All) in visit box area
 */
const numTabs = 5;

/*************************************** EXPORT STORE MODEL ***************************************/
/**
 * Store UI data for visit boxes (in My Records view)
 */
export class RecordVisitBoxesUIModel {
    /****** UI DATA PROPERTIES ******/
    /**
     * If true, record display acts like most Visit boxes collapsed
     * "Expand all" is displayed if true, and "Collapse all" if false
     */
    @observable
    public isCollapsed: boolean[] = arrayN(numTabs, false);

    /**
     * Sort direction for each tab
     */
    @observable
    public sortDirections: UpOrDown[] = [...arrayN<'down'>(numTabs - 1, `down`), `up`];

    /**
     * For each idx (each PageType):
     *   @prop {boolean} isInit true = the corresponding collapsedList val is initialized
     *   @prop {boolean} data   true = collapsed, false = expanded
     */
    @observable
    public collapsedList: Array<{isInit: boolean; data: boolean[]}> = arrayN(numTabs, {
        isInit: false,
        data: [],
    });

    /**
     * Currently active visit type tab (childhood, adult, travel, other, or all)
     */
    @computed
    public get activeVisitTypeTab(): TabIDs {
        return this.pagesModel.activePageType;
    }

    /**
     * True if tab has been switched at least once
     */
    @observable
    public hasTabSwitched = false;

    /****** COMPUTED & VIRTUAL PROPERTIES ******/
    /**
     * Generate a visit box ID for the given visit box & tab type
     */
    public genVisitBoxId = (visitTypeTab: TabIDs, title: string) =>
        `${visitTypeTab.dashCase()}-${title.dashCase()}`;

    @computed
    public get currentPageType(): PageType {
        return this.pagesModel.currentPageType;
    }

    @computed
    public get curPageIdx(): number {
        return this.pagesModel.currentPageType + 1;
    }

    @computed
    public get sortDirectionCurrentTab(): UpOrDown {
        return this.sortDirections[this.curPageIdx];
    }

    @computed
    public get isAllCollapsedCurrentTab(): boolean {
        return this.isCollapsed[this.curPageIdx];
    }

    @computed
    public get visitsCollapsedListCurrentTab(): boolean[] {
        return this.collapsedList[this.curPageIdx].data;
    }

    /**
     * Access the pages of the currently active record, with sort direction accounted for
     */
    @computed
    public get sortedPages(): PageInstance[] {
        const pages = this.recordModel.currentPages;
        return this.sortDirections[this.curPageIdx] === `down` ? pages : pages.slice().reverse();
    }

    /**
     * Get a list of all doses from associated record
     */
    @computed
    public get allDoses(): DoseInstance[] {
        return this.recordModel.record.doses;
    }

    /****** INITIALIZATION ******/
    /**
     * Factory method for RecordVisitBoxesUiModel
     * Store reference to record model being rendered
     */
    public static new = (recordModel: RecordModel, pagesModel: PagesModel) =>
        new RecordVisitBoxesUIModel(recordModel, pagesModel);

    private constructor(public recordModel: RecordModel, private pagesModel: PagesModel) {
        this.buildCollapsedStates(this.sortedPages);
    }

    /**
     * Initialize 'collapsed' vs 'expanded' arrays
     */
    @action
    public buildCollapsedStates = (pages?: PageInstance[] | DoseGroup[]): void => {
        const cleanPages = pages || this.sortedPages;
        const numPages = (cleanPages && cleanPages.length) || 0;
        const existingLen = this.collapsedList[this.curPageIdx].data.length;

        // Determine if this list was init; if not, do init sequence then block future init
        if (!this.collapsedList[this.curPageIdx].isInit) {
            this.collapsedList[this.curPageIdx].isInit = true;
            this.collapsedList[this.curPageIdx].data = Array(numPages).fill(false);
        } else if (existingLen < numPages) {
            // If list was init, only initialize the uninitialized items
            for (let i = existingLen; i < numPages; i++) {
                this.collapsedList[this.curPageIdx].data[i] = false;
            }
        }
    };

    /****** SETTERS ******/
    /**
     * Set currently active visit type tab (childhood, adult, travel, other, all)
     */
    @action
    public setActiveVisitTypeTab = (activeVisitTypeTab: TabIDs): void => {
        if (!this.hasTabSwitched) this.hasTabSwitched = true;
        this.pagesModel.setCurrentPageType(activeVisitTypeTab);
    };

    /****** ACTIONS ******/
    /**
     * USES: curPageIdx, sortDirections, collapsedList
     * Toggle sort direction - if `up`, make it `down`; and vice versa
     */
    @action
    public switchSortDirection = (): void => {
        const {curPageIdx, sortDirections, collapsedList} = this;

        // Switch locally stored currently selected sort direction (`up` <-> `down`)
        if (sortDirections[curPageIdx] && sortDirections[curPageIdx] === `down`) {
            sortDirections[curPageIdx] = `up`;
        } else if (sortDirections[curPageIdx] && sortDirections[curPageIdx] === `up`) {
            sortDirections[curPageIdx] = `down`;
        }

        // Sort list
        collapsedList[curPageIdx].data = collapsedList[curPageIdx].data.reverse();
    };

    /**
     * USES: curPageIdx, collapsedList, isCollapsed
     *
     * Collapse or expand a Visit/Page card at a single [index]
     * @param {Int} idx Index of a single Page/Visit to collapse or expand
     */
    @action
    public toggleCollapseIdx = (index: Int): void => {
        const {curPageIdx, collapsedList, isCollapsed} = this;
        const collapsedListData = collapsedList[curPageIdx].data;

        // Toggle the specific clicked page/visit.
        collapsedList[curPageIdx].data[index] = !collapsedListData[index];

        // Check if every page is collapsed or expanded and alter status accordingly
        if (isCollapsed[curPageIdx]) {
            if (collapsedListData.every(col => !col)) isCollapsed[curPageIdx] = false;
        } else {
            if (collapsedListData.every(col => !!col)) isCollapsed[curPageIdx] = true;
        }
    };

    /**
     * USES: isCollapsed, curPageIdx, collapsedList
     * Collapse (or expand) all Visit/Page cards
     */
    @action
    public collapseOrExpandAll = (): void => {
        const {isCollapsed, curPageIdx, collapsedList} = this;
        const numPages = collapsedList[curPageIdx].data.length;

        // Set all to either collapsed or expanded (depending on current state)
        for (let idx = 0; idx < numPages; idx++) {
            if (isCollapsed[curPageIdx]) collapsedList[curPageIdx].data[idx] = false;
            if (!isCollapsed[curPageIdx]) collapsedList[curPageIdx].data[idx] = true;
        }

        // Invert current tab's collapsation status (isCollapsed goes true to false & vice versa)
        isCollapsed[curPageIdx] = !isCollapsed[curPageIdx];
    };
}
